import LocalStorageService from "services/LocalStorageService";
import api from "services/api";
import * as Sentry from "@sentry/react";

class PushNotificationService {
  static sendPushNotification = async ({
    notification,
  }: {
    notification: string;
  }) => {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;

    if (!serviceWorkerRegistration) {
      throw new Error("ServiceWorkerRegistration not found");
    }
    let subscription =
      await serviceWorkerRegistration.pushManager.getSubscription();

    if (!subscription) {
      await PushNotificationService.unsubscribePushNotification();
      throw new Error("Subscription not found");
    }

    try {
      await api.post(`/send-notification`, {
        subscription,
        notification,
      });
    } catch (error) {
      console.error("Failed to send push notification");
      Sentry.captureException(error);
    }
  };

  static initPushSubscription = async () => {
    try {
      const serviceWorkerRegistration = await navigator.serviceWorker.ready;
      if (!serviceWorkerRegistration) {
        throw new Error("ServiceWorkerRegistration not found");
      }

      let subscription =
        await serviceWorkerRegistration.pushManager.getSubscription();
      if (!subscription) {
        const { data } = await api.get(`/vapid-public-key`);
        const vapidPublicKey = data;

        if (!vapidPublicKey) {
          throw new Error("Vapid public key not found");
        }

        subscription = await serviceWorkerRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidPublicKey,
        });

        if (!subscription) return;

        const token = LocalStorageService.getAccessToken();
        if (!token) return;

        await api.post(`/subscribe`, {
          subscription,
          userAgent: navigator.userAgent,
        });

        return;
      }

      const subscriptionServer =
        await PushNotificationService.getNotificationSubscription();

      const savedSubscription = subscriptionServer.data.subscription;

      const subscriptionRes = savedSubscription?.includes("endpoint")
        ? JSON.parse(subscriptionServer.data.subscription)
        : null;

      if (
        !subscriptionRes ||
        subscriptionRes?.endpoint !== subscription?.endpoint
      ) {
        await api.post(`/unsubscribe`, {
          userAgent: navigator.userAgent,
        });
        await api.post(`/subscribe`, {
          subscription,
          userAgent: navigator.userAgent,
        });
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  static unsubscribePushNotification = async () => {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;

    if (!serviceWorkerRegistration) {
      throw new Error("ServiceWorkerRegistration not found");
    }

    const subscription =
      await serviceWorkerRegistration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
    }

    await api.post(`/unsubscribe`, {
      userAgent: navigator.userAgent,
    });
  };

  static getNotificationSubscription = async (): Promise<{
    data: { subscription: string };
    status: number;
  }> => {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;

    if (!serviceWorkerRegistration) {
      throw new Error("ServiceWorkerRegistration not found");
    }

    return api.post(`/subscribe-status`, {
      userAgent: navigator.userAgent,
    });
  };
}

export default PushNotificationService;
